/**
 * The data panel's **Congress** tab: Senate and House trades in one stock, from
 * their periodic transaction reports (`docs/plans/congress-trades.md`; the
 * approved text mock is there).
 *
 * Three tiles — buys, sells, members — then the trades, newest disclosure
 * first. **90 days by default, a 1 year switch** (owner, 2026-10-14); stocks
 * only. Amounts are the filed ranges, never a midpoint: a member reports
 * "$15,001 – $50,000" and nothing narrower exists.
 *
 * **One component for every width.** Below `TABLE_WIDTH` — a phone's Data tab,
 * a narrow dock — each trade is two lines instead of a row of eight columns.
 * Measured rather than asked of the host, so no shell needs to know.
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactElement, type RefObject } from "react";

import type { CongressSymbol, CongressTrade } from "../api/congress";
import {
  WINDOWS, districtLabel, memberName, rangeLabel, shortDay, totalLabel, typeLabel, useCongress,
} from "../lib/congress-view";

interface Props {
  symbol: string;
  compact?: boolean;
}

/** Below this the trades are two lines each rather than a table. */
export const TABLE_WIDTH = 560;

export function CongressView({ symbol }: Props): ReactElement {
  const [days, setDays] = useState<number>(WINDOWS[0].days);
  const load = useCongress(symbol, days);
  const [host, width] = useWidth();
  return (
    <div ref={host} style={SCROLL} data-testid="congress-view">
      <div style={HEAD}>
        <div style={ASOF}>{symbol} · disclosed in the last {days === 90 ? "90 days" : "year"} · stocks only</div>
        <div role="group" aria-label="Window" style={{ display: "flex", gap: 4 }}>
          {WINDOWS.map((w) => (
            <button key={w.days} type="button" aria-pressed={days === w.days} onClick={() => setDays(w.days)}
                    style={seg(days === w.days)}>
              {w.label}
            </button>
          ))}
        </div>
      </div>
      {load.state === "loading" && <div style={MUTED}>loading…</div>}
      {load.state === "none" && (
        <div style={MUTED}>Congress trades are not available here yet — the data has not reached this server.</div>
      )}
      {load.state === "ready" && <Body c={load.data} wide={width >= TABLE_WIDTH} onYear={() => setDays(366)} />}
    </div>
  );
}

function Body({ c, wide, onYear }: { c: CongressSymbol; wide: boolean; onYear: () => void }): ReactElement {
  const s = c.summary;
  return (
    <>
      <div style={TILES}>
        <Tile label="Buys" count={s.buys} sub={totalLabel(s.buyLow, s.buyHigh, s.buys)} color="var(--qf-positive)" />
        <Tile label="Sells" count={s.sells} sub={totalLabel(s.sellLow, s.sellHigh, s.sells)} color="var(--qf-negative)" />
        <Tile label="Members" count={s.members} sub={`House ${s.house} · Sen ${s.senate}`} />
      </div>
      {c.trades.length === 0 ? (
        <div style={MUTED}>
          No member of Congress disclosed a trade in {c.symbol} in this window.
          {c.days < 366 && (
            <> <button type="button" onClick={onYear} style={LINK_BUTTON}>Try 1 year</button></>
          )}
        </div>
      ) : wide ? (
        <Table trades={c.trades} />
      ) : (
        <List trades={c.trades} />
      )}
      <div style={FOOT}>
        Ranges as filed. ⚠ disclosed more than 45 days after the trade. ↗ opens the filing.
      </div>
    </>
  );
}

function Tile({ label, count, sub, color }: { label: string; count: number; sub: string; color?: string }): ReactElement {
  return (
    <div style={TILE}>
      <div style={{ fontSize: 12, color: "var(--qf-text-muted)" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: color ?? "var(--qf-text)" }}>{count}</div>
      <div style={{ fontFamily: MONO, fontSize: 12, color: "var(--qf-text-secondary)" }}>{sub}</div>
    </div>
  );
}

function Side({ t }: { t: CongressTrade }): ReactElement {
  const color = t.side === "buy" ? "var(--qf-positive)" : t.side === "sell" ? "var(--qf-negative)" : "var(--qf-text-secondary)";
  const mark = t.side === "buy" ? "▲ " : t.side === "sell" ? "▼ " : "";
  return <span style={{ color, whiteSpace: "nowrap" }}>{mark}{typeLabel(t)}</span>;
}

function Late({ t }: { t: CongressTrade }): ReactElement | null {
  return t.late ? <span title="Disclosed more than 45 days after the trade" style={{ color: "var(--qf-warn)" }}> ⚠</span> : null;
}

function Filing({ t }: { t: CongressTrade }): ReactElement | null {
  if (!t.link) return null;
  return (
    <a href={t.link} target="_blank" rel="noreferrer noopener" title="Open the filing" style={{ color: "var(--qf-accent)", textDecoration: "none" }}>
      ↗
    </a>
  );
}

function Table({ trades }: { trades: CongressTrade[] }): ReactElement {
  return (
    <table style={TABLE}>
      <thead>
        <tr>
          {["Disclosed", "Traded", "Member", "Ch", "Owner", "Type", "Amount", ""].map((h, i) => (
            <th key={i} style={{ ...TH, textAlign: h === "Amount" ? "right" : "left" }}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {trades.map((t, i) => (
          <tr key={i} style={i % 2 ? { background: "var(--qf-row-alt)" } : undefined}>
            <td style={TD_MONO}>{shortDay(t.disclosed)}</td>
            <td style={TD_MONO}>{shortDay(t.traded)}<Late t={t} /></td>
            <td style={TD}>
              {memberName(t)} <span style={{ color: "var(--qf-text-muted)" }}>{districtLabel(t.district)}</span>
            </td>
            <td style={TD}>{t.chamber === "Senate" ? "S" : "H"}</td>
            <td style={TD}>{t.owner || "—"}</td>
            <td style={TD}><Side t={t} /></td>
            <td style={{ ...TD_MONO, textAlign: "right" }}>{rangeLabel(t.amountLow, t.amountHigh, t.amount)}</td>
            <td style={TD}><Filing t={t} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The narrow form: what and how much on the first line, who and when on the second. */
function List({ trades }: { trades: CongressTrade[] }): ReactElement {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {trades.map((t, i) => (
        <div key={i} style={{ ...ITEM, borderTop: i ? "1px solid var(--qf-border)" : 0 }}>
          <div style={LINE}>
            <Side t={t} />
            <span style={{ fontFamily: MONO }}>{rangeLabel(t.amountLow, t.amountHigh, t.amount)}</span>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textAlign: "right" }}>
              {memberName(t)}
            </span>
          </div>
          <div style={{ ...LINE, fontSize: 12, color: "var(--qf-text-muted)" }}>
            <span>
              {t.chamber === "Senate" ? "Senate" : `House ${districtLabel(t.district)}`}
              {t.owner ? ` · ${t.owner}` : ""}
            </span>
            <span style={{ flex: 1 }} />
            <span style={{ fontFamily: MONO }}>
              disclosed {shortDay(t.disclosed)} · traded {shortDay(t.traded)}
            </span>
            <Late t={t} />
            <Filing t={t} />
          </div>
        </div>
      ))}
    </div>
  );
}

function useWidth(): [RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? 0));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const SCROLL: CSSProperties = { padding: "8px 10px", display: "flex", flexDirection: "column", gap: 10, overflowY: "auto", height: "100%", color: "var(--qf-text)", fontSize: 14, boxSizing: "border-box" };
const HEAD: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" };
const ASOF: CSSProperties = { fontFamily: MONO, fontSize: 12, color: "var(--qf-text-muted)" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 13 };
const TILES: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 };
const TILE: CSSProperties = { border: "1px solid var(--qf-border)", borderRadius: 6, padding: "6px 8px", background: "var(--qf-surface-alt)", minWidth: 0 };
const TABLE: CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 13 };
const TH: CSSProperties = { fontWeight: 500, fontSize: 12, color: "var(--qf-text-muted)", padding: "4px 6px", borderBottom: "1px solid var(--qf-border)", whiteSpace: "nowrap" };
const TD: CSSProperties = { padding: "4px 6px", whiteSpace: "nowrap" };
const TD_MONO: CSSProperties = { ...TD, fontFamily: MONO, fontSize: 12 };
const ITEM: CSSProperties = { padding: "6px 0", display: "flex", flexDirection: "column", gap: 2 };
const LINE: CSSProperties = { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 };
const FOOT: CSSProperties = { fontSize: 11, color: "var(--qf-text-muted)" };
const LINK_BUTTON: CSSProperties = { background: "none", border: 0, padding: 0, color: "var(--qf-accent)", cursor: "pointer", font: "inherit" };

function seg(on: boolean): CSSProperties {
  return {
    padding: "2px 8px", fontSize: 12, cursor: "pointer", whiteSpace: "nowrap", borderRadius: 4,
    background: "transparent", border: `1px solid ${on ? "var(--qf-accent)" : "var(--qf-border)"}`,
    color: on ? "var(--qf-accent)" : "var(--qf-text-secondary)",
  };
}
